import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Upload, Check, Store, Palette, Clock, ChefHat,
  Utensils, Plus, CreditCard, Truck, MessageCircle, Star, Sparkles,
  Send, Loader2, Bot, X, HelpCircle
} from "lucide-react";
import { RESTAURANT_TEMPLATES } from "@/data/menuTemplates";
import { COUNTRY_CONFIG } from "@/utils/localization";
import { getCountryDefaults, COUNTRY_DEFAULTS, getOnboardingStrings } from "@/config/regionDefaults";

const MARKET_GROUPS = [
  { id: "eu", label: "Europa", flag: "🇪🇺", currency: "EUR", countries: [
    { code: "ES", flag: "🇪🇸", label: "Espanha" },
    { code: "PT", flag: "🇵🇹", label: "Portugal" },
    { code: "FR", flag: "🇫🇷", label: "França" },
    { code: "DE", flag: "🇩🇪", label: "Alemanha" },
    { code: "IT", flag: "🇮🇹", label: "Itália" },
  ]},
  { id: "br", label: "Brasil", flag: "🇧🇷", currency: "BRL", countries: [
    { code: "BR", flag: "🇧🇷", label: "Brasil" },
  ]},
  { id: "us", label: "EUA / CA", flag: "🇺🇸", currency: "USD", countries: [
    { code: "US", flag: "🇺🇸", label: "EUA" },
    { code: "CA", flag: "🇨🇦", label: "Canadá" },
  ]},
  { id: "ae", label: "Dubai", flag: "🇦🇪", currency: "AED", countries: [
    { code: "AE", flag: "🇦🇪", label: "Emirados Árabes" },
  ]},
  { id: "int", label: "Internacional", flag: "🌍", currency: "USD", countries: [
    { code: "US", flag: "🌍", label: "Internacional" },
  ]},
];

function getMarketForCountry(code) {
  return MARKET_GROUPS.find(m => m.countries.some(c => c.code === code)) || MARKET_GROUPS[0];
}

function detectCountry() {
  const lang = (navigator.language || "es").toLowerCase();
  if (lang === "pt-br") return "BR";
  if (lang.startsWith("pt")) return "PT";
  if (lang.startsWith("es")) return "ES";
  if (lang.startsWith("fr")) return "FR";
  if (lang.startsWith("de")) return "DE";
  if (lang.startsWith("it")) return "IT";
  if (lang.startsWith("ar")) return "AE";
  if (lang.startsWith("en")) return "US";
  return "ES";
}

const STEP_ICONS = [
  { icon: Store, color: "bg-blue-500" },
  { icon: Upload, color: "bg-violet-500" },
  { icon: Utensils, color: "bg-orange-500" },
  { icon: Palette, color: "bg-pink-500" },
  { icon: Clock, color: "bg-cyan-500" },
  { icon: CreditCard, color: "bg-emerald-500" },
  { icon: Truck, color: "bg-amber-500" },
  { icon: Star, color: "bg-rose-500" },
  { icon: Check, color: "bg-emerald-600" },
];

const DEFAULT_HOURS = [
  { day: "lunes", enabled: true, slots: [["12:00", "15:00"], ["19:00", "23:00"]] },
  { day: "martes", enabled: true, slots: [["12:00", "15:00"], ["19:00", "23:00"]] },
  { day: "miercoles", enabled: true, slots: [["12:00", "15:00"], ["19:00", "23:00"]] },
  { day: "jueves", enabled: true, slots: [["12:00", "15:00"], ["19:00", "23:00"]] },
  { day: "viernes", enabled: true, slots: [["12:00", "15:30"], ["19:00", "23:30"]] },
  { day: "sabado", enabled: true, slots: [["12:00", "15:30"], ["19:00", "23:30"]] },
  { day: "domingo", enabled: true, slots: [["12:00", "15:30"], ["19:00", "23:00"]] },
];

const STEP_TIPS = [
  "Preencha os dados do seu restaurante. O CIF/NIF e obrigatorio para emissao de notas fiscais.",
  "Suba seu logotipo. Recomendamos uma imagem quadrada com pelo menos 400x400 pixels.",
  "Escolha um template para comecar rapido. As categorias e produtos sao apenas exemplos - voce pode editar tudo depois.",
  "Escolha as cores que representam sua marca. A cor principal aparece nos botoes e destaques do menu.",
  "Configure os horarios de funcionamento. Voce pode ter dois turnos por dia (almoco e jantar).",
  "Configure como seus clientes vao pagar. Ative os metodos de pagamento que voce aceita.",
  "Ative delivery e/ou retirada. Configure a taxa de entrega padrao.",
  "Ative o programa de fidelidade para seus clientes acumularem pontos e ganharem recompensas.",
  "Parabens! Seu restaurante esta configurado. Agora edite o menu com seus produtos reais.",
];

const VISUAL_GUIDES = [
  { // Step 0: Restaurant Info
    steps: [
      { icon: Store, text: "Preencha o nome do restaurante", highlight: "Este nome aparece no menu publico" },
      { icon: "map-pin", text: "Adicione o endereco completo", highlight: "Usado para calcular delivery" },
      { icon: "phone", text: "Telefone para contato", highlight: "Clientes podem ligar direto" },
      { icon: "file-text", text: "CIF/NIF obrigatorio", highlight: "Necessario para notas fiscais" },
    ]
  },
  { // Step 1: Logo
    steps: [
      { icon: Upload, text: "Clique na area para subir logo", highlight: "Formatos: PNG, JPG (max 5MB)" },
      { icon: "image", text: "Recomendado: 400x400px", highlight: "Imagem quadrada fica melhor" },
      { icon: "circle", text: "Escolha formato: Redondo ou Quadrado", highlight: "Aparece no topo do menu" },
    ]
  },
  { // Step 2: Template
    steps: [
      { icon: ChefHat, text: "Escolha o tipo do seu restaurante", highlight: "Sushi, Hamburgueria, Pizzaria..." },
      { icon: "layers", text: "Categorias e produtos de exemplo", highlight: "Voce edita tudo depois" },
      { icon: "zap", text: "Menu pronto em 1 clique!", highlight: "Template aplica tudo automaticamente" },
    ]
  },
  { // Step 3: Colors
    steps: [
      { icon: Palette, text: "Cor principal = botoes e destaques", highlight: "Escolha a cor da sua marca" },
      { icon: "layout", text: "Cor de fundo do menu", highlight: "Claro fica mais legivel" },
      { icon: "type", text: "Cor do texto", highlight: "Escuro em fundo claro, claro em fundo escuro" },
    ]
  },
  { // Step 4: Hours
    steps: [
      { icon: Clock, text: "Marque os dias que abre", highlight: "Desmarque dias de folga" },
      { icon: "sun", text: "Almoco: 12:00 - 15:00", highlight: "Primeiro turno" },
      { icon: "moon", text: "Jantar: 19:00 - 23:00", highlight: "Segundo turno" },
    ]
  },
  { // Step 5: Payments
    steps: [
      { icon: "banknote", text: "Dinheiro: pagamento no local", highlight: "Sempre recomendado" },
      { icon: CreditCard, text: "Cartao via Stripe", highlight: "Configure chave Stripe em Ajustes > Pagamentos" },
      { icon: "smartphone", text: "Bizum (Espanha)", highlight: "Configure numero em Ajustes > Pagamentos" },
    ]
  },
  { // Step 6: Delivery
    steps: [
      { icon: "store", text: "Retirada: cliente busca no local", highlight: "Sem custo de entrega" },
      { icon: Truck, text: "Delivery: entrega no endereco", highlight: "Defina taxa e raio de entrega" },
      { icon: "map", text: "Zona de entrega no mapa", highlight: "Configure depois em Ajustes > Envios" },
    ]
  },
  { // Step 7: Loyalty
    steps: [
      { icon: Star, text: "Clientes ganham pontos por compra", highlight: "Ex: 1 ponto por EUR gasto" },
      { icon: "gift", text: "Troque pontos por recompensas", highlight: "Configure em Marketing > Fidelidade" },
      { icon: "repeat", text: "Clientes voltam mais!", highlight: "Aumento medio de 23% no retorno" },
    ]
  },
  { steps: [] }, // Step 8: Done
];

function VisualGuide({ stepIndex }) {
  const guide = VISUAL_GUIDES[stepIndex];
  if (!guide || !guide.steps.length) return null;

  return (
    <div className="mb-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-3 border border-gray-100" data-testid="visual-guide">
      <div className="flex items-center gap-1.5 mb-2.5">
        <HelpCircle className="w-3.5 h-3.5 text-orange-500" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-orange-600">Como funciona</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {guide.steps.map((s, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-[140px] bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm animate-in"
            style={{ animationDelay: `${i * 150}ms`, animation: `slideUp 0.4s ease-out ${i * 150}ms both` }}
            data-testid={`visual-guide-step-${i}`}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-bold text-orange-600">{i + 1}</div>
              <span className="text-[10px] font-semibold text-gray-800 leading-tight">{s.text}</span>
            </div>
            <p className="text-[9px] text-gray-400 leading-snug">{s.highlight}</p>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function AIAssistantInline({ authAxios, stepIndex }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    setMessages([{ role: "assistant", content: STEP_TIPS[stepIndex] || "Como posso ajudar?" }]);
  }, [stepIndex]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await authAxios.post("/ai/chat", { message: text, context: "support" });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Erro ao processar. Tente novamente." }]);
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-[60] bg-emerald-600 hover:bg-emerald-700 text-white rounded-full p-3 shadow-lg flex items-center gap-2 transition-all"
        data-testid="onboarding-ai-toggle"
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-bold pr-1">AI</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col" style={{ maxHeight: "400px" }} data-testid="onboarding-ai-chat">
      <div className="bg-emerald-600 text-white px-4 py-2.5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-bold">Assistente IA</span>
        </div>
        <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] text-xs px-3 py-2 rounded-xl ${m.role === "user" ? "bg-emerald-100 text-emerald-900" : "bg-gray-100 text-gray-800"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start"><div className="bg-gray-100 px-3 py-2 rounded-xl"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div></div>
        )}
        <div ref={endRef} />
      </div>
      <div className="p-2 border-t flex gap-1.5 flex-shrink-0">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Pergunte algo..."
          className="text-xs h-8"
          data-testid="onboarding-ai-input"
        />
        <Button size="sm" onClick={send} disabled={loading} className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-700" data-testid="onboarding-ai-send">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function OnboardingWizard({ open, onClose, authAxios, restaurant, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [data, setData] = useState({
    name: restaurant?.name || "",
    address: restaurant?.address || "",
    phone: restaurant?.phone || "",
    cif: restaurant?.cif || "",
    country: restaurant?.country || detectCountry(),
    logo_url: "",
    logo_format: "round",
    primary_color: "#f97316",
    bg_color: "#ffffff",
    text_color: "#111827",
    hours: restaurant?.business_hours?.length ? restaurant.business_hours : DEFAULT_HOURS,
    pickup_enabled: restaurant?.pickup_enabled !== false,
    delivery_enabled: restaurant?.delivery_enabled || false,
    default_shipping_fee: restaurant?.default_shipping_fee || 2.50,
    payment_cash: true,
    payment_card: true,
    payment_bizum: false,
    payment_mbway: false,
    loyalty_enabled: true,
    points_per_euro: 1,
  });

  const u = (key, val) => setData(p => ({ ...p, [key]: val }));

  // Derive language and translated strings from selected country
  const lang = COUNTRY_DEFAULTS[data.country?.toUpperCase()]?.ui_language || "es";
  const wiz = getOnboardingStrings(lang);
  // Build STEPS with translated titles/descs at render time
  const STEPS = STEP_ICONS.map((s, i) => ({ ...s, title: wiz.steps[i].title, desc: wiz.steps[i].desc }));

  // BLOCO 1 — when country changes in step 0, auto-apply regional defaults.
  // Only fires when the user explicitly changes the country selector.
  // Does NOT overwrite fields the user has already manually edited.
  // BLOCO 2 — also reset payment method toggles to country-appropriate defaults.
  const prevCountryRef = useRef(data.country);
  useEffect(() => {
    if (data.country === prevCountryRef.current) return;
    prevCountryRef.current = data.country;
    // Payment method defaults by country: Bizum = ES only, MBWay = PT only
    setData(p => ({
      ...p,
      payment_bizum: data.country === "ES",
      payment_mbway: data.country === "PT",
    }));
  }, [data.country]);

  const uploadLogo = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await authAxios.post("/restaurant/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      u("logo_url", res.data.logo_url);
      await authAxios.patch("/marketing/settings", { logo_url: res.data.logo_url });
      toast.success(wiz.labels.logoUploaded);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar logo");
    }
  };

  const saveStep = async () => {
    setSaving(true);
    try {
      switch (step) {
        case 0: { // Restaurant info
          if (!data.name || !data.address || !data.phone || !data.cif) {
            toast.error(wiz.labels.errorRequired);
            setSaving(false); return;
          }
          const countryConf = COUNTRY_CONFIG[data.country];
          if (countryConf && !countryConf.fiscalDocument.validate(data.cif)) {
            toast.error(countryConf.fiscalDocument.errorMessage);
            setSaving(false); return;
          }
          // BLOCO 1: include regional defaults when saving country choice.
          const defs = getCountryDefaults(data.country);
          await authAxios.patch("/restaurant", {
            name: data.name, address: data.address, phone: data.phone, cif: data.cif,
            country: data.country,
            market: getMarketForCountry(data.country).id,
            display_currency: defs.display_currency,
            timezone: defs.timezone,
            locale: defs.locale,
            phone_prefix: defs.phone_prefix,
          });
          break;
        }
        case 1: // Logo
          await authAxios.patch("/marketing/settings", { logo_url: data.logo_url, logo_format: data.logo_format });
          break;
        case 2: // Template
          if (selectedTemplate && selectedTemplate !== "custom") {
            const tpl = RESTAURANT_TEMPLATES.find(t => t.id === selectedTemplate);
            if (tpl) {
              await authAxios.post("/menu/apply-template", { categories: tpl.categories });
              toast.success(`Template "${tpl.name}" aplicado!`);
            }
          }
          break;
        case 3: // Colors
          await authAxios.patch("/marketing/settings", {
            menu_colors: { primary: data.primary_color, bg: data.bg_color, text: data.text_color, secondary: "#1f2937", accent: "#CDA34F", header_bg: "#ffffff", category: "", price: "" }
          });
          break;
        case 4: // Hours
          await authAxios.patch("/restaurant", { business_hours: data.hours });
          break;
        case 5: // Payments — BLOCO 2: include only country-appropriate methods
          await authAxios.patch("/restaurant", {
            payment_methods: [
              ...(data.payment_cash ? ["cash"] : []),
              ...(data.payment_card ? ["card"] : []),
              // Bizum = Spain only; MBWay = Portugal only
              ...(data.payment_bizum && data.country === "ES" ? ["bizum"] : []),
              ...(data.payment_mbway && data.country === "PT" ? ["mbway"] : []),
            ],
          });
          break;
        case 6: // Delivery — both can be disabled (table/POS-only restaurants)
          await authAxios.patch("/restaurant", {
            pickup_enabled: data.pickup_enabled,
            delivery_enabled: data.delivery_enabled,
            default_shipping_fee: data.delivery_enabled ? data.default_shipping_fee : null,
          });
          break;
        case 7: // Loyalty
          await authAxios.patch("/loyalty/config", {
            enabled: data.loyalty_enabled,
            points_per_euro: data.points_per_euro,
          });
          break;
        default: break;
      }
      setStep(s => s + 1);
    } catch (e) {
      toast.error(wiz.labels.errorSave);
    }
    setSaving(false);
  };

  const renderStep = () => {
    switch (step) {
      case 0: {
        const countryFiscal = (COUNTRY_CONFIG[data.country] || COUNTRY_CONFIG["ES"]).fiscalDocument;
        const phonePlaceholder = COUNTRY_DEFAULTS[data.country?.toUpperCase()]?.phone_prefix
          ? `${COUNTRY_DEFAULTS[data.country.toUpperCase()].phone_prefix} 600 000 000`
          : "+34 600 000 000";
        return (
          <div className="space-y-4" data-testid="onboard-step-info">
            {/* Market / Region selector */}
            {(() => {
              const activeMarket = getMarketForCountry(data.country);
              // When a specific country is selected within a multi-country market,
              // highlight that country's market group; single-country markets always match.
              return (
                <div className="space-y-2">
                  <Label>{wiz.labels.market} *</Label>
                  <div className="grid grid-cols-5 gap-1.5" data-testid="onboard-market-selector">
                    {MARKET_GROUPS.map(market => (
                      <button
                        key={market.id}
                        type="button"
                        onClick={() => u("country", market.countries[0].code)}
                        className={`flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-lg border-2 text-center transition ${activeMarket.id === market.id ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                        data-testid={`onboard-market-${market.id}`}
                      >
                        <span className="text-lg">{market.id === "eu" && data.country !== market.countries[0].code
                          ? (MARKET_GROUPS[0].countries.find(c => c.code === data.country)?.flag ?? market.flag)
                          : market.flag}</span>
                        <span className="text-[9px] font-semibold leading-tight">{market.label}</span>
                        <span className="text-[8px] text-gray-400">{market.currency}</span>
                      </button>
                    ))}
                  </div>
                  {activeMarket.countries.length > 1 && (
                    <div className="flex gap-2 flex-wrap mt-1" data-testid="onboard-country-selector">
                      {activeMarket.countries.map(c => (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => u("country", c.code)}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition ${data.country === c.code ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 hover:border-gray-300 text-gray-600"}`}
                          data-testid={`onboard-country-${c.code.toLowerCase()}`}
                        >
                          <span>{c.flag}</span>
                          <span>{c.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="space-y-2">
              <Label>{wiz.labels.restaurantName} *</Label>
              <Input value={data.name} onChange={e => u("name", e.target.value)} placeholder="Meu Restaurante" className="h-11" data-testid="onboard-name" />
            </div>
            <div className="space-y-2">
              <Label>{wiz.labels.address} *</Label>
              <Input value={data.address} onChange={e => u("address", e.target.value)} placeholder="Rua Principal 123, Cidade" className="h-11" data-testid="onboard-address" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{wiz.labels.phone} *</Label>
                <Input value={data.phone} onChange={e => u("phone", e.target.value)} placeholder={phonePlaceholder} className="h-11" data-testid="onboard-phone" />
              </div>
              <div className="space-y-2">
                <Label>{countryFiscal.name} *</Label>
                <Input value={data.cif} onChange={e => u("cif", e.target.value)} placeholder={countryFiscal.placeholder} className="h-11" data-testid="onboard-cif" />
                <p className="text-[10px] text-gray-400">{countryFiscal.errorMessage}</p>
              </div>
            </div>
          </div>
        );
      }
      case 1: return (
        <div className="space-y-4" data-testid="onboard-step-logo">
          <p className="text-sm text-gray-500">Suba o logotipo do seu restaurante. Aceita imagens ate 5MB.</p>
          <div className="flex items-center justify-center gap-8 py-3">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); }} />
              <div className={`w-24 h-24 border-2 border-dashed rounded-full flex flex-col items-center justify-center transition ${data.logo_url ? "border-emerald-400 bg-emerald-50/30" : "border-gray-300 hover:border-orange-400"}`} data-testid="onboard-logo-upload">
                {data.logo_url ? (
                  <img src={data.logo_url} alt="Logo" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <><Upload className="w-6 h-6 text-gray-400 mb-1" /><span className="text-[10px] text-gray-400">Upload</span></>
                )}
              </div>
            </label>
          </div>
          <div className="flex justify-center gap-4">
            <button onClick={() => u("logo_format", "round")} className={`px-4 py-2 text-xs rounded-full border-2 ${data.logo_format === "round" ? "border-orange-500 bg-orange-50 font-bold" : "border-gray-200"}`} data-testid="onboard-logo-round">{wiz.labels.logoRound}</button>
            <button onClick={() => u("logo_format", "square")} className={`px-4 py-2 text-xs rounded-lg border-2 ${data.logo_format === "square" ? "border-orange-500 bg-orange-50 font-bold" : "border-gray-200"}`} data-testid="onboard-logo-square">{wiz.labels.logoSquare}</button>
          </div>
          {data.logo_url && <p className="text-xs text-emerald-600 text-center">{wiz.labels.logoUploaded}</p>}
        </div>
      );
      case 2: return (
        <div className="space-y-3" data-testid="onboard-step-template">
          <p className="text-sm text-gray-500">Escolha o tipo de restaurante para criar categorias e produtos de exemplo.</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[220px] overflow-y-auto pr-1">
            {RESTAURANT_TEMPLATES.map(t => (
              <button key={t.id} onClick={() => setSelectedTemplate(t.id)}
                className={`p-2.5 rounded-xl border-2 text-center transition hover:shadow-sm ${selectedTemplate === t.id ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid={`onboard-tpl-${t.id}`}
              >
                <span className="text-xl block mb-0.5">{t.icon}</span>
                <span className="text-[10px] font-medium leading-tight block">{t.name}</span>
                <span className="text-[9px] text-gray-400">{t.categories.length} cat.</span>
              </button>
            ))}
            <button onClick={() => setSelectedTemplate("custom")}
              className={`p-2.5 rounded-xl border-2 border-dashed text-center transition ${selectedTemplate === "custom" ? "border-orange-500 bg-orange-50" : "border-gray-300"}`}
              data-testid="onboard-tpl-custom"
            >
              <Plus className="w-5 h-5 mx-auto mb-0.5 text-gray-400" />
              <span className="text-[10px] font-medium block">Outro</span>
            </button>
          </div>
          {selectedTemplate && selectedTemplate !== "custom" && (
            <div className="bg-gray-50 rounded-lg p-2.5">
              <p className="text-[10px] font-medium text-gray-700 mb-1">Categorias:</p>
              <div className="flex flex-wrap gap-1">
                {RESTAURANT_TEMPLATES.find(t => t.id === selectedTemplate)?.categories.map(c => (
                  <span key={c.name} className="text-[9px] bg-white border rounded px-1.5 py-0.5 text-gray-600">{c.name} ({c.products.length})</span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
      case 3: return (
        <div className="space-y-4" data-testid="onboard-step-colors">
          <p className="text-sm text-gray-500">Escolha as cores do seu menu digital.</p>
          <div className="grid grid-cols-3 gap-4">
            {[
              { key: "primary_color", label: "Principal" },
              { key: "bg_color", label: "Fundo" },
              { key: "text_color", label: "Texto" },
            ].map(c => (
              <div key={c.key} className="space-y-1.5 text-center">
                <Label className="text-xs">{c.label}</Label>
                <input type="color" value={data[c.key]} onChange={e => u(c.key, e.target.value)} className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200 mx-auto block" data-testid={`onboard-color-${c.key}`} />
              </div>
            ))}
          </div>
          <div className="border rounded-xl overflow-hidden">
            <div className="p-3 flex items-center gap-2" style={{ backgroundColor: data.bg_color }}>
              <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-white text-xs font-bold">KS</div>
              <span className="font-bold text-sm" style={{ color: data.text_color }}>{data.name || "Restaurante"}</span>
              <div className="ml-auto px-3 py-1 rounded-full text-white text-xs font-bold" style={{ backgroundColor: data.primary_color }}>Pedir</div>
            </div>
          </div>
        </div>
      );
      case 4: return (
        <div className="space-y-2" data-testid="onboard-step-hours">
          <p className="text-xs text-gray-500">Defina os horários de funcionamento. Pode alterar depois em Ajustes.</p>
          <div className="space-y-1.5 max-h-[250px] overflow-y-auto pr-1">
            {data.hours.map((h, dayIdx) => (
              <div key={h.day} className="bg-gray-50 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={h.enabled}
                    onChange={() => {
                      const newH = [...data.hours]; newH[dayIdx] = { ...h, enabled: !h.enabled }; u("hours", newH);
                    }}
                    className="w-4 h-4 accent-orange-500 flex-shrink-0"
                    data-testid={`onboard-hours-${h.day}`}
                  />
                  <span className="text-xs font-semibold flex-1">{wiz.days[h.day] || h.day}</span>
                  {h.enabled && (
                    <button
                      type="button"
                      onClick={() => u("hours", data.hours.map(d => ({ ...d, enabled: h.enabled, slots: h.slots.map(s => [...s]) })))}
                      className="text-[9px] text-orange-500 hover:text-orange-700 font-medium whitespace-nowrap"
                    >
                      {wiz.labels.applyToAll}
                    </button>
                  )}
                </div>
                {h.enabled && (
                  <div className="mt-1.5 ml-6 space-y-1">
                    {h.slots.map((slot, slotIdx) => (
                      <div key={slotIdx} className="flex items-center gap-1.5">
                        <input
                          type="time"
                          value={slot[0]}
                          onChange={e => {
                            const newH = data.hours.map((d, di) => {
                              if (di !== dayIdx) return d;
                              return { ...d, slots: d.slots.map((s, si) => si === slotIdx ? [e.target.value, s[1]] : s) };
                            });
                            u("hours", newH);
                          }}
                          className="h-7 text-xs border border-gray-200 rounded px-1.5 w-[82px] bg-white"
                          data-testid={`onboard-hours-${h.day}-slot${slotIdx}-start`}
                        />
                        <span className="text-gray-400 text-xs">—</span>
                        <input
                          type="time"
                          value={slot[1]}
                          onChange={e => {
                            const newH = data.hours.map((d, di) => {
                              if (di !== dayIdx) return d;
                              return { ...d, slots: d.slots.map((s, si) => si === slotIdx ? [s[0], e.target.value] : s) };
                            });
                            u("hours", newH);
                          }}
                          className="h-7 text-xs border border-gray-200 rounded px-1.5 w-[82px] bg-white"
                          data-testid={`onboard-hours-${h.day}-slot${slotIdx}-end`}
                        />
                        {h.slots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newH = data.hours.map((d, di) => {
                                if (di !== dayIdx) return d;
                                return { ...d, slots: d.slots.filter((_, si) => si !== slotIdx) };
                              });
                              u("hours", newH);
                            }}
                            className="text-red-400 hover:text-red-600 text-xs leading-none"
                          >✕</button>
                        )}
                      </div>
                    ))}
                    {h.slots.length < 3 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newH = data.hours.map((d, di) => {
                            if (di !== dayIdx) return d;
                            return { ...d, slots: [...d.slots, ["19:00", "23:00"]] };
                          });
                          u("hours", newH);
                        }}
                        className="flex items-center gap-0.5 text-[10px] text-orange-500 hover:text-orange-700 font-medium mt-0.5"
                        data-testid={`onboard-hours-${h.day}-add-slot`}
                      >
                        <Plus className="w-3 h-3" /> {wiz.labels.addSlot}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
      case 5: return (
        <div className="space-y-4" data-testid="onboard-step-payments">
          <p className="text-sm text-gray-500">Ative os metodos de pagamento que voce aceita.</p>
          <div className="space-y-3">
            {/* Always shown */}
            {[
              { key: "payment_cash", label: wiz.labels.cashLabel, desc: wiz.labels.cashDesc, icon: "💵" },
              { key: "payment_card", label: wiz.labels.cardLabel, desc: wiz.labels.cardDesc, icon: "💳" },
            ].map(pm => (
              <div key={pm.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`onboard-pay-${pm.key}`}>
                <span className="text-2xl">{pm.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{pm.label}</p>
                  <p className="text-[11px] text-gray-500">{pm.desc}</p>
                </div>
                <Switch checked={data[pm.key]} onCheckedChange={v => u(pm.key, v)} data-testid={`onboard-pay-toggle-${pm.key}`} />
              </div>
            ))}
            {/* BLOCO 2: Bizum — Spain only */}
            {data.country === "ES" && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid="onboard-pay-payment_bizum">
                <span className="text-2xl">📱</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Bizum</p>
                  <p className="text-[11px] text-gray-500">{wiz.labels.bizumDesc}</p>
                </div>
                <Switch checked={data.payment_bizum} onCheckedChange={v => u("payment_bizum", v)} data-testid="onboard-pay-toggle-payment_bizum" />
              </div>
            )}
            {/* BLOCO 2: MBWay — Portugal only */}
            {data.country === "PT" && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid="onboard-pay-payment_mbway">
                <span className="text-2xl">📲</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">MBWay</p>
                  <p className="text-[11px] text-gray-500">{wiz.labels.mbwayDesc}</p>
                </div>
                <Switch checked={data.payment_mbway} onCheckedChange={v => u("payment_mbway", v)} data-testid="onboard-pay-toggle-payment_mbway" />
              </div>
            )}
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
            <HelpCircle className="w-3.5 h-3.5 inline mr-1" />
            {wiz.labels.paymentHint}
          </div>
        </div>
      );
      case 6: return (
        <div className="space-y-4" data-testid="onboard-step-delivery">
          <p className="text-sm text-gray-500">Configure as opcoes de entrega. Pode alterar a qualquer momento em Ajustes.</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid="onboard-delivery-pickup">
              <span className="text-2xl">🏪</span>
              <div className="flex-1">
                <p className="text-sm font-medium">{wiz.labels.pickupLabel}</p>
                <p className="text-[11px] text-gray-500">{wiz.labels.pickupDesc}</p>
              </div>
              <Switch checked={data.pickup_enabled} onCheckedChange={v => u("pickup_enabled", v)} data-testid="onboard-pickup-toggle" />
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-testid="onboard-delivery-delivery">
              <span className="text-2xl">🛵</span>
              <div className="flex-1">
                <p className="text-sm font-medium">{wiz.labels.deliveryLabel}</p>
                <p className="text-[11px] text-gray-500">{wiz.labels.deliveryDesc}</p>
              </div>
              <Switch checked={data.delivery_enabled} onCheckedChange={v => u("delivery_enabled", v)} data-testid="onboard-delivery-toggle" />
            </div>
          </div>
          {data.delivery_enabled && (
            <div className="space-y-2 pl-2 border-l-2 border-amber-300 ml-4">
              <Label className="text-xs">{wiz.labels.shippingFee}</Label>
              <Input type="number" step="0.50" min="0" value={data.default_shipping_fee} onChange={e => u("default_shipping_fee", parseFloat(e.target.value) || 0)} className="h-9 w-32" data-testid="onboard-shipping-fee" />
              <p className="text-[10px] text-gray-400">{wiz.labels.shippingHint}</p>
            </div>
          )}
          {!data.pickup_enabled && !data.delivery_enabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5" data-testid="onboard-delivery-both-disabled">
              <span className="text-lg flex-shrink-0">⚠️</span>
              <div>
                <p className="text-xs font-semibold text-amber-800">{wiz.labels.bothDisabledTitle}</p>
                <p className="text-[11px] text-amber-700 mt-0.5">{wiz.labels.bothDisabledDesc}</p>
              </div>
            </div>
          )}
        </div>
      );
      case 7: return (
        <div className="space-y-4" data-testid="onboard-step-loyalty">
          <p className="text-sm text-gray-500">{wiz.labels.loyaltyDesc}</p>
          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-rose-50 to-amber-50 rounded-xl border border-rose-200" data-testid="onboard-loyalty-box">
            <Star className="w-8 h-8 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold">{wiz.labels.loyaltyLabel}</p>
              <p className="text-xs text-gray-500">{wiz.labels.loyaltyDesc}</p>
            </div>
            <Switch checked={data.loyalty_enabled} onCheckedChange={v => u("loyalty_enabled", v)} data-testid="onboard-loyalty-toggle" />
          </div>
          {data.loyalty_enabled && (
            <div className="space-y-3 pl-4 border-l-2 border-rose-300">
              <div className="space-y-1">
                <Label className="text-xs">{wiz.labels.ptsPerEuro}</Label>
                <Input type="number" min="1" max="10" value={data.points_per_euro} onChange={e => u("points_per_euro", parseInt(e.target.value) || 1)} className="h-9 w-32" data-testid="onboard-pts-per-euro" />
              </div>
            </div>
          )}
        </div>
      );
      case 8: {
        // BLOCO 4: compute public URL from restaurant slug
        const _slug = restaurant?.slug;
        const _menuUrl = _slug ? `https://${_slug}.kaisosystem.com` : null;
        return (
          <div className="text-center py-2" data-testid="onboard-step-done">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3" style={{ animation: "scaleIn 0.5s ease-out" }}>
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{wiz.labels.doneTitle}</h3>
            <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">{wiz.labels.doneDesc}</p>

            {/* BLOCO 4: public URL card */}
            {_menuUrl && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 text-left" data-testid="onboard-public-url">
                <p className="text-[10px] font-bold uppercase text-emerald-700 mb-1.5">{wiz.labels.publicUrlLabel}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-white border border-emerald-200 rounded px-2 py-1.5 text-emerald-900 truncate">{_menuUrl}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(_menuUrl); toast.success(wiz.labels.copyLink + "!"); }}
                    className="shrink-0 px-2.5 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition"
                    data-testid="onboard-copy-url"
                  >{wiz.labels.copyLink}</button>
                  <a href={_menuUrl} target="_blank" rel="noopener noreferrer"
                    className="shrink-0 px-2.5 py-1.5 text-xs bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-700 rounded-lg font-semibold transition"
                    data-testid="onboard-open-url"
                  >{wiz.labels.openLink}</a>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 max-w-md mx-auto text-xs mb-3">
              {[
                { icon: Utensils, label: "Editar Menu", desc: "Adicione produtos reais", delay: "0ms" },
                { icon: MessageCircle, label: "WhatsApp", desc: "Notificacoes automaticas", delay: "150ms" },
                { icon: CreditCard, label: "Pagamentos", desc: "Stripe em Ajustes > Pagos", delay: "300ms" },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-2.5 text-center border border-gray-100" style={{ animation: `slideUp 0.4s ease-out ${item.delay} both` }}>
                  <item.icon className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
                  <span className="font-bold text-gray-700 block mb-0.5 text-[10px]">{item.label}</span>
                  <span className="text-gray-400 text-[9px]">{item.desc}</span>
                </div>
              ))}
            </div>
            <style>{`
              @keyframes scaleIn {
                from { opacity: 0; transform: scale(0.5); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        );
      }
      default: return null;
    }
  };

  const totalSteps = STEPS.length;
  const isLast = step === totalSteps - 1;
  const canSkip = step >= 5 && step <= 7; // Payments, delivery, loyalty can be skipped

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="onboarding-wizard">
          <DialogTitle className="sr-only">Configurar Restaurante</DialogTitle>

          {/* Progress */}
          <div className="px-6 pt-5">
            <div className="flex items-center gap-0">
              {STEPS.map((s, i) => (
                <div key={i} className="flex items-center flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${i < step ? "bg-emerald-500 text-white" : i === step ? `${s.color} text-white` : "bg-gray-200 text-gray-400"}`}>
                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  {i < totalSteps - 1 && <div className={`flex-1 h-0.5 mx-0.5 ${i < step ? "bg-emerald-400" : "bg-gray-200"}`} />}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-right">{step + 1} {wiz.labels.step} {totalSteps}</p>
          </div>

          {/* Header */}
          <div className="px-6 pt-1 pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                {(() => { const Icon = STEPS[step].icon; return <div className={`w-9 h-9 rounded-lg ${STEPS[step].color} flex items-center justify-center`}><Icon className="w-5 h-5 text-white" /></div>; })()}
                <div>
                  <h2 className="text-base font-bold text-gray-900">{STEPS[step].title}</h2>
                  <p className="text-xs text-gray-500">{STEPS[step].desc}</p>
                </div>
              </div>
              <button 
                onClick={() => onClose(false)} 
                className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-400 hover:text-gray-600"
                data-testid="onboard-close-btn"
                title="Fechar e configurar depois"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-3 min-h-[260px] max-h-[380px] overflow-y-auto">
            <VisualGuide stepIndex={step} />
            {renderStep()}
          </div>

          {/* Footer */}
          <div className="px-6 pb-5 flex items-center justify-between">
            {step > 0 && !isLast ? (
              <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} data-testid="onboard-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> {wiz.labels.back}
              </Button>
            ) : <div />}
            <div className="flex items-center gap-2">
              {canSkip && (
                <Button variant="ghost" size="sm" onClick={() => setStep(s => s + 1)} data-testid="onboard-skip" className="text-gray-400">
                  {wiz.labels.skip}
                </Button>
              )}
              {!isLast ? (
                <Button onClick={saveStep} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="onboard-next">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {step === totalSteps - 2 ? wiz.labels.finish : wiz.labels.next}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={() => { if (onComplete) onComplete(); onClose(false); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="onboard-finish">
                  {wiz.labels.done} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Assistant floating */}
      {open && <AIAssistantInline authAxios={authAxios} stepIndex={step} />}
    </>
  );
}
